import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import Dropzone from "react-dropzone";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Button, Col, Row, Spinner } from "reactstrap";
import * as Yup from "yup";
import { createCategory } from "../../../../../api/categoryAPIs";
import { Block, BlockBetween, BlockHead, BlockHeadContent, BlockTitle, PreviewCard } from "../../../../../components/Component";
import Content from "../../../../../layout/content/Content";

const CreateCategoryPage = () => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate()
    const initialValues = {
        name: "",
        description: "",
        images: null,
    };

    const validationSchema = Yup.object({
        name: Yup.string().required("Category name is required"),
        description: Yup.string().required("Description is required"),
        images: Yup.array()
            .min(1, "At least one image is required")
            .test(
                "fileType",
                "Only the following formats are accepted: .jpeg, .png, .jpg",
                (files) =>
                    files && files.every((file) =>
                        ["image/jpeg", "image/png", "image/jpg"].includes(file.type)
                    )
            ),
    });

    const handleDropChange = (acceptedFiles, setFieldValue) => {
        setFiles(
            acceptedFiles.map((file) =>
                Object.assign(file, { preview: URL.createObjectURL(file) })
            )
        );
        setFieldValue("images", acceptedFiles);
    };

    const handleSubmit = async (values, { resetForm }) => {
        if (!files.length) {
            toast.error("Please upload at least one image", { position: "top-center" });
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append(
                "data",
                JSON.stringify({ name: values.name, description: values.description })
            );
            formData.append("images", values.images[0]);

            const response = await createCategory({ formData });

            if (response.error) {
                toast.error(response.error, { position: "top-center" });
            } else {
                toast.success("Category created successfully", { position: "top-center" });
                resetForm();
                setFiles([]);
                navigate("/category")
            }
        } catch (error) {
            toast.error("Something went wrong. Please try again later.", { position: "top-center" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Content className="container mx-auto p-4">
            <Block>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Create Category
                            </BlockTitle>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>

                <PreviewCard>
                    <div className="rounded-lg p-6">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ setFieldValue }) => (
                                <Form>
                                    <Row className="g-3" xs={1}>
                                        <Col size="12">
                                            <div className="form-group">
                                                <label className="form-label" htmlFor="name">
                                                    Category Name
                                                </label>
                                                <Field
                                                    type="text"
                                                    className="form-control"
                                                    name="name"
                                                    placeholder="Enter category name"
                                                />
                                                <ErrorMessage name="name" component="span" className="invalid" />
                                            </div>
                                        </Col>

                                        <Col size="12">
                                            <div className="form-group">
                                                <label className="form-label" htmlFor="description">
                                                    Description
                                                </label>
                                                <Field
                                                    as="textarea"
                                                    className="form-control"
                                                    name="description"
                                                    placeholder="Enter category description"
                                                />
                                                <ErrorMessage name="description" component="span" className="invalid" />
                                            </div>
                                        </Col>

                                        <Col size="6">
                                            <div className="form-group">
                                                <label className="form-label">Category Image</label>
                                                <Dropzone
                                                    onDrop={(acceptedFiles) =>
                                                        handleDropChange(acceptedFiles, setFieldValue)
                                                    }
                                                >
                                                    {({ getRootProps, getInputProps }) => (
                                                        <section>
                                                            <div
                                                                {...getRootProps()}
                                                                className="dropzone upload-zone small bg-lighter my-2 dz-clickable"
                                                            >
                                                                <input {...getInputProps()} />
                                                                {files.length === 0 && (
                                                                    <p>
                                                                        Drag 'n' drop some files here, or click to select files
                                                                    </p>
                                                                )}
                                                                {files.map((file) => (
                                                                    <div
                                                                        key={file.name}
                                                                        className="dz-preview dz-processing dz-image-preview dz-error dz-complete"
                                                                    >
                                                                        <div className="dz-image">
                                                                            <img src={file.preview} alt="preview" />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </section>
                                                    )}
                                                </Dropzone>
                                                <ErrorMessage name="images" component="span" className="invalid" />
                                            </div>
                                        </Col>

                                        <Col size="12">
                                            <div className="d-flex justify-content-end">
                                                <Button color="primary" type="submit" disabled={loading}>
                                                    {loading ? <Spinner size="sm" /> : "Create Category"}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </PreviewCard>
            </Block>
        </Content>
    );
};

export default CreateCategoryPage;
